import { useAuthenticationMethods } from '@api/authMethods';
import { useTenant } from '@api/tenants';
import AppHeader from '@components/AppNavigation/AppHeader';
import HorizontalTabsPage from '@components/AppNavigation/HorizontalTabsPage';
import { SSOSetup } from '@components/AuthMethods';
import SSOMappingsPage from '@components/AuthMethods/SSO/Mappings/SSOMappingsPage';
import Loader from '@components/Loader/Loader';
import TenantEdit from '@components/Tenants/TenantEdit';
import { Link as MUILink } from '@mui/material';
import { Box } from '@mui/system';
import { APP_ROUTES } from 'constants/routes';
import { useNavigate } from 'hooks';
import React, { FC, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { TenantSettings } from 'types/tenants';

const SingleTenantPage: FC = () => {
	const navigate = useNavigate();
	const { tenantId } = useParams();
	const { data: authSettings } = useAuthenticationMethods();
	const { isLoading, data: tenant } = useTenant(tenantId);

	const canUseSAML = !!authSettings?.saml.enabled;

	useEffect(() => {
		if (!isLoading && !tenant) {
			navigate(APP_ROUTES.tenants);
		}
	}, [isLoading, tenant, navigate]);

	const tabs = useMemo(
		() => [
			{
				id: TenantSettings.details,
				component: (
					<Loader isLoading={isLoading} variant="profile">
						{tenant && <TenantEdit tenant={tenant!} />}
					</Loader>
				)
			},
			{
				id: TenantSettings.sso,
				component: <SSOSetup tenant={tenant} showAlert={!canUseSAML} />
			},
			{
				id: TenantSettings.mappings,
				component: <SSOMappingsPage tenant={tenant} />
			}
		],
		[tenant, isLoading, canUseSAML]
	);

	return (
		<Box
			data-testid="tenant-page"
			display="flex"
			flexGrow={1}
			flexDirection="column"
			position="relative"
		>
			<Box display="flex" flexDirection="row">
				<Link to={`${APP_ROUTES.tenants}`}>
					<MUILink sx={{ textDecoration: 'none' }} component="span">
						<AppHeader translation="pages.tenants.title" sx={{ mr: 0 }} />
					</MUILink>
				</Link>
				<AppHeader
					translation="pages.tenants.single.title.nest"
					sx={{ ml: 2 }}
					loading={isLoading}
					values={{
						name: tenant?.name
					}}
				/>
			</Box>
			<Box display="flex" flexGrow={1} overflow="hidden">
				<HorizontalTabsPage
					tabs={tabs}
					routeName="tenants"
					fullRoute={`${APP_ROUTES.tenants}/${tenantId}`}
				/>
			</Box>
		</Box>
	);
};

export default SingleTenantPage;
